import { cn } from '../cn.js';

export type StatusTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export interface StatusDotProps {
  readonly tone: StatusTone;
  /** The state, in words. **Required** — see below. */
  readonly label: string;
  /** Hides the text and leaves it to assistive technology. Only inside a dense, labelled column. */
  readonly labelHidden?: boolean;
  readonly className?: string;
}

const toneClass: Record<StatusTone, string> = {
  neutral: 'bg-(--color-text-muted)',
  accent: 'bg-(--color-accent)',
  success: 'bg-(--color-success)',
  warning: 'bg-(--color-warning)',
  danger: 'bg-(--color-danger)',
};

/**
 * A state, as a dot and a word.
 *
 * **The word is not optional**, because colour is never the only carrier of state — about one man
 * in twelve cannot reliably separate the red dot from the green one, and nobody can separate them
 * in a screenshot pasted into a monochrome report. `labelHidden` keeps the text for assistive
 * technology where a column header already says what the column is; it does not remove it.
 *
 * A tenant may replace the accent, which is another reason the dot alone cannot mean anything:
 * `accent` and `success` could be a similar hue on somebody's deployment.
 */
export function StatusDot({ tone, label, labelHidden = false, className }: StatusDotProps) {
  return (
    <span className={cn('inline-flex items-center gap-(--space-xs)', className)}>
      <span
        aria-hidden="true"
        className={cn('size-(--space-sm) shrink-0 rounded-(--radius-full)', toneClass[tone])}
      />
      <span className={labelHidden ? 'sr-only' : 'text-(length:--font-size-body)'}>{label}</span>
    </span>
  );
}
